import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { FileSpreadsheet, Layers, X } from 'lucide-react'
import {
  GenericSortHeader,
  dealTableHeadRowClass,
  dealTableRowClass,
} from '@/components/shared/DealTable'
import { dashboardsService, downloadDrillExport } from '@/services/dashboards'
import { useDashboardDrillStore } from '@/store/dashboardStore'
import type { DrillColumn, DrillResult, DrillRow } from '@/types/dashboards'
import { formatCell } from './drillCell'
import { PanelError, PanelSkeleton } from './Panel'
import { consumeDrillTrigger } from './useDrillDown'

const FOCUSABLE =
  'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'

function focusableWithin(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  )
}

/** Words left, measurements right — used for the header and the cells alike so
 *  a column never reads with its heading on the opposite edge. */
function columnAlign(column: DrillColumn): 'left' | 'right' | 'center' {
  if (column.align) return column.align
  return column.type === 'text' || column.type === 'date' || column.type === 'status'
    ? 'left'
    : 'right'
}

function cellAlign(column: DrillColumn): string {
  const align = columnAlign(column)
  return align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
}

/**
 * One modal for every board.
 *
 * Sorting and paging are server-side — the dialog holds a page of a record set
 * that can be far larger than the page, so sorting the visible rows would
 * silently reorder a slice and present it as the ranking.
 */
export default function DrillDownDialog() {
  const isOpen = useDashboardDrillStore((s) => s.isOpen)
  const descriptor = useDashboardDrillStore((s) => s.descriptor)
  const page = useDashboardDrillStore((s) => s.page)
  const pageSize = useDashboardDrillStore((s) => s.pageSize)
  const sort = useDashboardDrillStore((s) => s.sort)
  const closeDrill = useDashboardDrillStore((s) => s.closeDrill)
  const setPage = useDashboardDrillStore((s) => s.setPage)
  const toggleSort = useDashboardDrillStore((s) => s.toggleSort)

  const navigate = useNavigate()
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const closeRef = useRef<HTMLButtonElement | null>(null)
  const restoreRef = useRef<HTMLElement | null>(null)

  const [exportBusy, setExportBusy] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  const query = useQuery<DrillResult>({
    queryKey: ['dashboards', 'drill', descriptor?.dataset, descriptor?.filters, page, pageSize, sort],
    queryFn: ({ signal }) =>
      dashboardsService.drill(
        {
          dataset: descriptor?.dataset ?? '',
          filters: descriptor?.filters ?? {},
          page,
          pageSize,
          sort,
        },
        signal,
      ),
    enabled: isOpen && Boolean(descriptor),
  })

  // Escape closes, Tab cycles inside, and focus returns where it came from.
  useEffect(() => {
    if (!isOpen) return
    // The trigger is recorded by useDrillDown.open at click time. Under
    // StrictMode this effect runs twice, and the second pass would otherwise
    // read the close button this same effect just focused.
    restoreRef.current = consumeDrillTrigger() ?? restoreRef.current
    closeRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // No stopPropagation: this listener is already ON document, so there is
        // nothing further up to stop. Suppressing peers would need
        // stopImmediatePropagation, and silently swallowing another surface's
        // Escape is worse than letting it through.
        closeDrill()
        return
      }
      if (event.key !== 'Tab') return
      const nodes = focusableWithin(dialogRef.current)
      if (nodes.length === 0) return
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement
      const inside = dialogRef.current?.contains(active as Node) ?? false
      if (event.shiftKey && (!inside || active === first)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (!inside || active === last)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    const restoreTo = restoreRef.current
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      // Deferred on purpose. React runs effect cleanups in declaration order,
      // so when this one runs the effect below has not yet stripped `inert`
      // from #root — and focus() on an inert subtree is silently ignored,
      // dropping focus to <body>. A keyboard user closing the dialog would
      // land at the top of the document instead of the figure they opened.
      //
      // setTimeout rather than requestAnimationFrame: rAF callbacks do not run
      // while the page is not compositing (a background tab, or a headless
      // check), which would leave focus stranded exactly where it was lost.
      setTimeout(() => restoreTo?.focus?.(), 0)
    }
  }, [isOpen, closeDrill])

  // The page behind an aria-modal dialog must leave the accessibility tree, or
  // a screen reader walks straight out of the modal into the board it covers.
  // `inert` also removes the background from the tab order, which the Tab
  // handler above cannot do by itself. The dialog is portalled to <body> below
  // precisely so it sits OUTSIDE the shell this marks.
  useEffect(() => {
    if (!isOpen) return
    const shell = document.getElementById('root')
    if (!shell) return
    shell.setAttribute('inert', '')
    shell.setAttribute('aria-hidden', 'true')
    return () => {
      shell.removeAttribute('inert')
      shell.removeAttribute('aria-hidden')
    }
  }, [isOpen])

  useEffect(() => {
    setExportError(null)
  }, [descriptor])

  if (!isOpen || !descriptor) return null

  const result = query.data
  const columns = result?.columns ?? []
  const rows = result?.rows ?? []
  const total = result?.total ?? 0
  const hasWorkspace = rows.some((row) => typeof row.href === 'string' && row.href)
  const sortColumn = sort ? columns.findIndex((column) => column.key === sort.key) : -1
  const firstRow = total === 0 ? 0 : (page - 1) * pageSize + 1
  const lastRow = Math.min(page * pageSize, total)
  const lastPage = Math.max(1, Math.ceil(total / pageSize))

  const runExport = async () => {
    setExportBusy(true)
    setExportError(null)
    try {
      const outcome = await dashboardsService.drillExport({
        dataset: descriptor.dataset,
        filters: descriptor.filters,
        sort,
      })
      downloadDrillExport(outcome.blob, outcome.filename)
    } catch (err) {
      const detail = (err as { message?: string })?.message
      setExportError(`Export failed: ${detail ?? 'network error'}`)
    } finally {
      setExportBusy(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={closeDrill}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={descriptor.title || result?.title}
        className="relative flex flex-col rounded-xl border border-amber-400/15 bg-[#090909] shadow-2xl"
        style={{ width: '88%', height: '82%', maxWidth: 1180 }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex flex-shrink-0 items-center gap-3 border-b border-amber-400/10 px-4"
          style={{ height: 52 }}
        >
          <div className="min-w-0">
            {/* The figure's own title ("Andrew Fawer — Funded") leads: it says
                which number was clicked. The dataset's generic title ("Deals")
                is the subline, so the record set is still named. */}
            <div className="truncate text-[13px] font-semibold text-stone-100" title={descriptor.title || result?.title}>
              {descriptor.title || result?.title}
            </div>
            <div className="flex items-center gap-2 font-mono text-[10px] text-stone-500">
              <span>{result?.title && result.title !== descriptor.title ? result.title : descriptor.dataset}</span>
              {total > 0 && (
                <>
                  <span className="text-stone-700">·</span>
                  <span>
                    {firstRow.toLocaleString('en-US')}–{lastRow.toLocaleString('en-US')} of{' '}
                    {total.toLocaleString('en-US')}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {exportError && (
              <span className="max-w-[280px] truncate text-[10px] text-red-400" title={exportError}>
                {exportError}
              </span>
            )}
            <button
              type="button"
              onClick={() => void runExport()}
              disabled={exportBusy || total === 0}
              className="flex items-center gap-1 rounded border border-amber-400/25 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:cursor-wait disabled:opacity-40"
            >
              <FileSpreadsheet size={11} /> {exportBusy ? 'Generating…' : 'Excel'}
            </button>
            <button
              ref={closeRef}
              type="button"
              onClick={closeDrill}
              aria-label="Close records"
              className="rounded p-1 text-stone-500 transition-colors hover:bg-stone-800 hover:text-stone-200"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Records */}
        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin px-4 py-3">
          {query.error ? (
            <PanelError error={query.error} onRetry={() => void query.refetch()} />
          ) : query.isLoading ? (
            <PanelSkeleton rows={10} />
          ) : rows.length === 0 ? (
            <p className="px-1 py-6 text-center text-[12px] text-stone-500">
              No records match this figure in the current partition.
            </p>
          ) : (
            <table className="dense-table">
              <thead>
                <tr className={dealTableHeadRowClass()}>
                  {columns.map((column, index) => (
                    <GenericSortHeader
                      key={column.key}
                      label={column.label}
                      columnIndex={index}
                      sortColumn={sortColumn >= 0 ? sortColumn : null}
                      sortDir={sort?.dir ?? 'desc'}
                      onSort={(columnIndex) => toggleSort(columns[columnIndex].key)}
                      align={columnAlign(column)}
                    />
                  ))}
                  {hasWorkspace && <th className="w-[130px] text-right">Workspace</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((row: DrillRow, rowIndex) => (
                  <tr
                    key={String(row.id ?? `${page}-${rowIndex}`)}
                    className={dealTableRowClass(false, 'cursor-default')}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className={clsx('tabular-nums', cellAlign(column))}>
                        {formatCell(row[column.key], column)}
                      </td>
                    ))}
                    {hasWorkspace && (
                      <td className="text-right">
                        {typeof row.href === 'string' && row.href && (
                          <button
                            type="button"
                            onClick={() => {
                              closeDrill()
                              navigate(row.href as string)
                            }}
                            className="inline-flex items-center gap-1 rounded border border-amber-400/25 px-1.5 py-0.5 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10"
                          >
                            <Layers size={10} /> Open in workspace
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              {/* `{}` is truthy: a dataset that declares no footer totals would
                  otherwise render a "Total" row of blank cells. */}
              {result?.totals && Object.keys(result.totals).length > 0 && (
                <tfoot>
                  <tr className="border-t border-amber-400/15">
                    {columns.map((column, index) => (
                      <td
                        key={column.key}
                        className={clsx('font-medium text-stone-200', cellAlign(column))}
                      >
                        {index === 0
                          ? 'Total'
                          : result.totals?.[column.key] != null
                            ? formatCell(result.totals[column.key], column)
                            : ''}
                      </td>
                    ))}
                    {hasWorkspace && <td />}
                  </tr>
                </tfoot>
              )}
            </table>
          )}
        </div>

        {/* Paging */}
        <div className="flex flex-shrink-0 items-center gap-2 border-t border-amber-400/10 px-4 py-2">
          <span className="font-mono text-[10px] text-stone-500">
            Page {page} of {lastPage}
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1 || query.isFetching}
              className="rounded border border-amber-400/15 px-2 py-1 text-[10px] text-stone-300 transition-colors hover:border-amber-400/40 hover:text-amber-200 disabled:opacity-30"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= lastPage || query.isFetching}
              className="rounded border border-amber-400/15 px-2 py-1 text-[10px] text-stone-300 transition-colors hover:border-amber-400/40 hover:text-amber-200 disabled:opacity-30"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
